import React, { useMemo, ReactElement } from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldDate from '../../../components/form-field-date/form-field-date.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import FormatService from '../../../services/format-service';

const AssetPropertyPerformance: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    formError,
    assetData,
    onCancelForm,
    onSubmitForm,
  } = props;

  const formPrefix: string = 'assetData';
  const parsedAtCloseNoiAndOccupancyDate: Date = assetData && assetData.atCloseNoiAndOccupancyDate
    && new Date(assetData.atCloseNoiAndOccupancyDate);
  const parsedFirstExtendedMaturityDate: Date = assetData && assetData.firstExtendedMaturityDate
    && new Date(assetData.firstExtendedMaturityDate);
  const parsedSecondExtendedMaturityDate: Date = assetData && assetData.secondExtendedMaturityDate
    && new Date(assetData.secondExtendedMaturityDate);
  const parsedThirdExtendedMaturityDate: Date = assetData && assetData.thirdExtendedMaturityDate
    && new Date(assetData.thirdExtendedMaturityDate);

  const atCloseNoiAndOccupancyDate: string = useMemo((): string => (
    parsedAtCloseNoiAndOccupancyDate && FormatService.formatDateString(parsedAtCloseNoiAndOccupancyDate)
  ), [parsedAtCloseNoiAndOccupancyDate]);
  const firstExtendedMaturityDate: string = useMemo((): string => (
    parsedFirstExtendedMaturityDate && FormatService.formatDateString(parsedFirstExtendedMaturityDate)
  ), [parsedFirstExtendedMaturityDate]);
  const secondExtendedMaturityDate: string = useMemo((): string => (
    parsedSecondExtendedMaturityDate && FormatService.formatDateString(parsedSecondExtendedMaturityDate)
  ), [parsedSecondExtendedMaturityDate]);
  const thirdExtendedMaturityDate: string = useMemo((): string => (
    parsedThirdExtendedMaturityDate && FormatService.formatDateString(parsedThirdExtendedMaturityDate)
  ), [parsedThirdExtendedMaturityDate]);

  const isEmpty: boolean = !isEditMode && !assetData.futureNoi && !assetData.stabilizedAppraisedNoi
    && !assetData.stabilizedOccupancy && !assetData.stabilizedCapitalizationRate && !assetData.atCloseRevenue
    && !assetData.atCloseExpenses && !assetData.atCloseNoi && !assetData.atCloseDebtYieldNoi
    && !assetData.atCloseDscrNoi && !assetData.atCloseNcf && !assetData.atCloseDebtYieldNcf
    && !assetData.atCloseDscrNcf && !assetData.occupancyAtOrigination && !assetData.atCloseNoiAndOccupancyDate
    && !assetData.stabilizedNoiAtOrigination && !assetData.stabilizedNcfAtOrigination
    && !assetData.firstExtendedMaturityDate && !assetData.secondExtendedMaturityDate
    && !assetData.thirdExtendedMaturityDate;

  return (
    <SinglePageTab
      isSaving={isSaving}
      isEmpty={isEmpty}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      {
        (!isEmpty || isEditMode) && (
          <Table>
            <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
              {
                (isEditMode || assetData.futureNoi) && (
                  <TableRow>
                    <TableCell>
                      Future NOI
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.futureNoi`} />
                        ) : assetData.futureNoi
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.stabilizedAppraisedNoi) && (
                  <TableRow>
                    <TableCell>
                      Stabilized Appraised NOI
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.stabilizedAppraisedNoi`} />
                        ) : assetData.stabilizedAppraisedNoi
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.stabilizedOccupancy) && (
                  <TableRow>
                    <TableCell>
                      Stabilized Occupancy
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.stabilizedOccupancy`} />
                        ) : assetData.stabilizedOccupancy
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.stabilizedCapitalizationRate) && (
                  <TableRow>
                    <TableCell>
                      Stabilized Capitalization Rate
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.stabilizedCapitalizationRate`} />
                        ) : assetData.stabilizedCapitalizationRate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.atCloseRevenue) && (
                  <TableRow>
                    <TableCell>
                      At Close Revenue
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.atCloseRevenue`} />
                        ) : assetData.atCloseRevenue
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.atCloseExpenses) && (
                  <TableRow>
                    <TableCell>
                      At Close Expenses
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.atCloseExpenses`} />
                        ) : assetData.atCloseExpenses
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.atCloseNoi) && (
                  <TableRow>
                    <TableCell>
                      At Close NOI
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.atCloseNoi`} />
                        ) : assetData.atCloseNoi
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.atCloseDebtYieldNoi) && (
                  <TableRow>
                    <TableCell>
                      At Close Debt Yield NOI
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.atCloseDebtYieldNoi`} />
                        ) : assetData.atCloseDebtYieldNoi
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.atCloseDscrNoi) && (
                  <TableRow>
                    <TableCell>
                      At Close DSCR NOI
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.atCloseDscrNoi`} />
                        ) : assetData.atCloseDscrNoi
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.atCloseNcf) && (
                  <TableRow>
                    <TableCell>
                      At Close NCF
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.atCloseNcf`} />
                        ) : assetData.atCloseNcf
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.atCloseDebtYieldNcf) && (
                  <TableRow>
                    <TableCell>
                      At Close Debt Yield NCF
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.atCloseDebtYieldNcf`} />
                        ) : assetData.atCloseDebtYieldNcf
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.atCloseDscrNcf) && (
                  <TableRow>
                    <TableCell>
                      At Close DSCR NCF
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.atCloseDscrNcf`} />
                        ) : assetData.atCloseDscrNcf
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.occupancyAtOrigination) && (
                  <TableRow>
                    <TableCell>
                      Occupancy at Origination
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isFloat isBordered name={`${formPrefix}.occupancyAtOrigination`} />
                        ) : assetData.occupancyAtOrigination
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || atCloseNoiAndOccupancyDate) && (
                  <TableRow>
                    <TableCell>
                      At Close NOI and Occupancy Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate
                            isBordered
                            withBlankValues
                            name={`${formPrefix}.atCloseNoiAndOccupancyDate`}
                          />
                        ) : atCloseNoiAndOccupancyDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.stabilizedNoiAtOrigination) && (
                  <TableRow>
                    <TableCell>
                      Stabilized NOI at Origination
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.stabilizedNoiAtOrigination`} />
                        ) : assetData.stabilizedNoiAtOrigination
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.stabilizedNcfAtOrigination) && (
                  <TableRow>
                    <TableCell>
                      Stabilized NCF at Origination
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.stabilizedNcfAtOrigination`} />
                        ) : assetData.stabilizedNcfAtOrigination
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || firstExtendedMaturityDate) && (
                  <TableRow>
                    <TableCell>
                      1st Extended Maturity Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate isBordered withBlankValues name={`${formPrefix}.firstExtendedMaturityDate`} />
                        ) : firstExtendedMaturityDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || secondExtendedMaturityDate) && (
                  <TableRow>
                    <TableCell>
                      2nd Extended Maturity Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate isBordered withBlankValues name={`${formPrefix}.secondExtendedMaturityDate`} />
                        ) : secondExtendedMaturityDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || thirdExtendedMaturityDate) && (
                  <TableRow>
                    <TableCell>
                      3rd Extended Maturity Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate isBordered withBlankValues name={`${formPrefix}.thirdExtendedMaturityDate`} />
                        ) : thirdExtendedMaturityDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
            </TableBody>
          </Table>
        )
      }
    </SinglePageTab>
  );
});

export default AssetPropertyPerformance;
